import type { z } from 'zod';

/*
 * API-client.
 *
 * Yksi sisaankaynti kaikille app -> worker HTTP-kutsuille:
 *
 *   const data = await apiFetch<HealthResponse>('/health');
 *   const data = await apiFetch('/me', { schema: UserSchema });
 *
 * Vastuut:
 *  1. Resolvoi API_BASE_URL ympariston perusteella (prod / dev / override).
 *  2. Liittaa Authorization-headerin jos JWT-tokeni on muistissa.
 *  3. Kasittelee 401:n: yrittaa refresh-tokenia kerran (Sessio 5b:n alkaen
 *     POST /auth/refresh -kutsulla), sitten heittaa UnauthorizedErrorin.
 *  4. Validoi vastauksen Zod-skeemalla jos se annetaan optionissa.
 *  5. Lahettaa cookie:t kaikissa pyynnoissa (`credentials: 'include'`) jotta
 *     refresh-cookie luviamo_refresh kulkee `Path=/auth`-pyyntoihin.
 *
 * Refresh-flow (Sessio 5b):
 *  - Kun pyynto saa 401:n, kutsutaan tryRefreshToken() joka tekee POST /auth/refresh.
 *  - Useat samanaikaiset 401:t odottavat samaa singleton-promisea (race condition -esto).
 *  - Onnistunut refresh -> dispatch 'auth:refreshed' -event jossa { user, organization }.
 *    AuthContext kuuntelee tata ja paivittaa tilan.
 *  - Epaonnistunut refresh -> dispatch 'auth:unauthorized' -event. AuthContext
 *    asettaa status='unauthenticated' ja ProtectedRoute redirektaa /login:iin.
 *
 * Tokenia EI tallenneta localStorageen XSS-syiden takia. Token elaa muistissa
 * ja katoaa kun valilehti suljetaan; refresh-eviyste (httpOnly + Secure +
 * SameSite=Strict + Path=/auth, 7 pv) elvyttaa session backend:in kautta.
 */

/* ---- Ympariston resolvointi ---- */

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string | undefined) ??
  (import.meta.env.PROD
    ? 'https://api.luviamo.app'
    : 'http://localhost:8787');

/* ---- Virhetyypit ---- */

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export class UnauthorizedError extends ApiError {
  constructor(body: unknown) {
    super('Unauthorized', 401, body);
    this.name = 'UnauthorizedError';
  }
}

export class ValidationError extends Error {
  issues: unknown;
  constructor(issues: unknown) {
    super('Response validation failed');
    this.name = 'ValidationError';
    this.issues = issues;
  }
}

/* ---- Token-tila (in-memory) ---- */

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

/* ---- Kielitila (in-memory) ----
 *
 * Palvelin lukee kutsujan kielen Accept-Language-otsikosta (route-context buildAuthContext) ja
 * palauttaa sen mukaan ne tekstit joita se itse tuottaa: budjetin oletusdimension nimi, "Yleinen"-
 * kori, PDF-vientien otsikot. Selaimen oma Accept-Language kertoo SELAIMEN kielen, ei sovelluksen
 * kielivalitsimen tilaa -- suomenkielisellä selaimella EN-käyttöliittymä sai suomenkielisiä
 * palvelintekstejä (9.9.2026). Siksi otsikko asetetaan tästä, i18n:n kielestä.
 */
let apiLanguage: 'fi' | 'en' = 'fi';

export function setApiLanguage(language: string): void {
  apiLanguage = language.startsWith('en') ? 'en' : 'fi';
}

export function getAccessToken(): string | null {
  return accessToken;
}

/* ---- Refresh-flow (Sessio 5b: oikea POST /auth/refresh -kutsu) ---- */

/**
 * AuthContext kuuntelee 'auth:refreshed' / 'auth:unauthorized' -eventeja
 * (window.dispatchEvent). Tama irrottaa apiFetch:n React-komponenteista --
 * apiFetch toimii myos React Routerin / TanStack Queryn ulkopuolella.
 */
export type AuthRefreshedDetail = {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    org_id: string;
    /** Sessio 6b: ISO-aikaleima jolloin email vahvistettiin, null jos ei viela */
    email_verified_at: string | null;
  };
  organization: {
    id: string;
    name: string;
    plan: string;
    /** Sessio 8b: trial-jakson loppu (SQLite-formaatti `YYYY-MM-DD HH:MM:SS`) tai null. */
    trial_ends_at: string | null;
    /** Sessio 8b: Stripe-tilauksen tila (trialing/active/past_due/canceled/unpaid) tai null trial-tilassa. */
    subscription_status: string | null;
    /** Sessio 8b: maksun epaonnistumisen armonajan loppu (7 pv) tai null. */
    grace_period_until: string | null;
  };
};

/**
 * Singleton-promise estaa race conditionit: jos useat samanaikaiset caller:it
 * (apiFetch:n 401-handler, AuthProvider:n bootstrap-useEffect, OAuthCallbackPage:n
 * useEffect, Strict Mode -double-mount) kutsuvat refreshia samanaikaisesti, kaikki
 * odottavat **samaa** POST /auth/refresh -kutsua. Tama on kriittista koska backend:n
 * replay-detection (Sessio 5a) mitatoi koko sessio-perheen jos rotatoitua tokenia
 * yritetaan kayttaa uudelleen -- duplikaatti-pyynto laukaisisi sen vahingossa.
 *
 * Sessio 6a:n e2e-testi paljasti taman: OAuth-callbackissa AuthProvider mountaa
 * juuressa + OAuthCallbackPage mountaa lapsessa, molemmat kutsuvat bootstrapRefresh.
 * Aiemmin tryRefreshToken ja bootstrapRefresh olivat erilliset toteutukset,
 * vain tryRefreshToken oli singleton-suojattu.
 */
let inflightRefresh: Promise<AuthRefreshedDetail | null> | null = null;

/**
 * Yksi piste joka tekee POST /auth/refresh. Caller:it (tryRefreshToken,
 * bootstrapRefresh) kayttavat tata jaettua singletonia.
 */
async function performRefresh(): Promise<AuthRefreshedDetail | null> {
  if (inflightRefresh) return inflightRefresh;

  inflightRefresh = (async (): Promise<AuthRefreshedDetail | null> => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) return null;
      const json = (await response.json()) as {
        access_token: string;
        user: AuthRefreshedDetail['user'];
        organization: AuthRefreshedDetail['organization'];
      };
      setAccessToken(json.access_token);
      return { user: json.user, organization: json.organization };
    } catch {
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

/**
 * apiFetch:n 401-handler kutsuu tata. Dispatchaa auth:refreshed / auth:unauthorized
 * -eventin lopputuloksen mukaan jotta AuthContext + muut React-komponentit
 * voivat reagoida.
 */
async function tryRefreshToken(): Promise<boolean> {
  const result = await performRefresh();
  if (typeof window !== 'undefined') {
    if (result) {
      window.dispatchEvent(
        new CustomEvent<AuthRefreshedDetail>('auth:refreshed', { detail: result }),
      );
    } else {
      window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    }
  }
  return result !== null;
}

/**
 * Suora refresh-kutsu AuthContext:n bootstrap-flowille (sivulataus / F5) ja
 * OAuthCallbackPage:lle. Palauttaa user+org tiedot caller:lle synkroonisesti
 * tilan rakentamiseen.
 *
 * Jakaa saman inflightRefresh-singletonin tryRefreshToken:n kanssa: jos AuthProvider
 * ja OAuthCallbackPage kutsuvat samaan aikaan, molemmat saavat saman vastauksen
 * yhdesta POST /auth/refresh -kutsusta.
 */
export async function bootstrapRefresh(): Promise<AuthRefreshedDetail | null> {
  return performRefresh();
}

/* ---- Pakatun fetch-funktion tyypitys ---- */

type ApiFetchOptions<T> = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  /* Input-tyyppi on unknown: skeema saa sisältää .default/.catch-kenttiä (In ≠ Out), esim.
     AdsCampaignStat.provider (MA-2 deploy-turva) — parse tuottaa silti tarkan T:n. */
  schema?: z.ZodType<T, z.ZodTypeDef, unknown>;
  signal?: AbortSignal;
};

/* ---- Paafunktio ---- */

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions<T> = {},
): Promise<T> {
  const { method = 'GET', body, headers = {}, schema, signal } = options;

  const url = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;

  const finalHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'Accept-Language': apiLanguage,
    ...headers,
  };

  if (accessToken) {
    finalHeaders.Authorization = `Bearer ${accessToken}`;
  }

  const doFetch = (): Promise<Response> =>
    fetch(url, {
      method,
      headers: finalHeaders,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal,
      credentials: 'include',
    });

  let response = await doFetch();

  if (response.status === 401) {
    // Refresh-flow vain jos kayttajalla on aiemmin ollut access-token (eli sessio
    // vanhentui). Login/register-pyynnoissa accessToken on null -> 401 etenee
    // suoraan UnauthorizedError-poikkeuksena ilman tryRefreshToken-yritysta
    // (cookie puuttuu, refresh palauttaisi vain toisen 401:n).
    if (accessToken) {
      const refreshed = await tryRefreshToken();
      if (refreshed && accessToken) {
        finalHeaders.Authorization = `Bearer ${accessToken}`;
        response = await doFetch();
      }
    }
    if (response.status === 401) {
      const errBody = await safeJson(response);
      throw new UnauthorizedError(errBody);
    }
  }

  const responseBody = await safeJson(response);

  if (!response.ok) {
    throw new ApiError(
      `API ${method} ${path} failed (${response.status})`,
      response.status,
      responseBody,
    );
  }

  if (schema) {
    const parsed = schema.safeParse(responseBody);
    if (!parsed.success) {
      throw new ValidationError(parsed.error.issues);
    }
    return parsed.data;
  }

  return responseBody as T;
}

/* ---- Apurit ---- */

async function safeJson(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/* ---- API_BASE_URL ulospain debugia varten ---- */

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}
